import React from 'react';
import Header from '@amzn/awsui-components-console/header';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import Popover from '@amzn/awsui-components-console/popover';
import Icon from '@amzn/awsui-components-console/icon';
import { StringVariant } from 'src/generated/graphql';

interface VariantsTableProps {
  variants: StringVariant[];
  /** Base path to construct variant links (adds ?variant={cxId}) */
  baseUrl: string;
  /**
   * Pinned string group version from the current page's ?version= param, if
   * any. Forwarded onto variant links so navigating to a variant preserves
   * the pinned version instead of silently jumping back to latest.
   */
  versionParam?: string;
}

const VariantsTable: React.FC<VariantsTableProps> = ({
  variants,
  baseUrl,
  versionParam,
}) => {
  const buildVariantHref = (cxId: string): string => {
    const query = new URLSearchParams();
    query.set('variant', cxId);
    if (versionParam != null) {
      query.set('version', versionParam);
    }
    return `${baseUrl}?${query.toString()}`;
  };

  return (
    <Table
      header={
        <Header
          counter={variants.length > 0 ? `(${variants.length})` : undefined}
          info={
            <Popover
              triggerType="text"
              header="Manage Variants"
              content="Variants are CX ID-specific overrides of the base string."
              triggerAriaLabel="Info about manage variants"
            >
              <Icon name="status-info" size="small" />
            </Popover>
          }
        >
          Manage Variants
        </Header>
      }
      items={variants}
      columnDefinitions={[
        {
          id: 'cxId',
          header: 'CX ID',
          cell: (item) => (
            <span data-testid="variant-row" data-cxid={item.cxId}>
              <span data-testid="variant-cxid">
                <Link href={buildVariantHref(item.cxId)}>{item.cxId}</Link>
              </span>
            </span>
          ),
        },
        {
          id: 'sourceText',
          header: 'Variant Source Text',
          cell: (item) => (
            <span data-testid="variant-row-source-text">{item.sourceText}</span>
          ),
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) => (
            <Link
              href={buildVariantHref(item.cxId)}
              data-testid="variant-view-details"
            >
              View Details
            </Link>
          ),
        },
      ]}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="l">
          No variants defined for this string.
        </Box>
      }
    />
  );
};

export default VariantsTable;
